import React, { useMemo, useEffect } from "react";
import { Select } from "@mantine/core";
import { useForms } from "@/hooks/useForms";
import { LoadingIndicator } from "@/components/generic";
import { Form } from "@/utils/api";
import { useApp } from "@/contexts";
import { STATUSES } from "@sdflc/utils";

export interface FormSelectorProps {
  /** Selected form ID */
  value: string | null | undefined;

  /** Callback when form selection changes */
  onChange?: (formId: string | null, form?: Form) => void;

  /** Placeholder text */
  placeholder?: string;

  /** Whether to include inactive forms */
  includeInactive?: boolean;

  /** Custom filter function for forms */
  formFilter?: (form: Form) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text */
  emptyText?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Additional props passed to Select */
  [key: string]: any;
}

export const FormSelector: React.FC<FormSelectorProps> = ({
  value,
  onChange,
  placeholder = "Select a form",
  includeInactive = false,
  formFilter,
  required = false,
  loadingPlaceholder = "Loading forms...",
  emptyText = "No forms available",
  error,
  disabled,
  size = "md",
  ...selectProps
}) => {
  const { isAuthenticated } = useApp();
  const { forms, loading, isEmpty, lastError } = useForms({
    autoLoad: true,
    isAuthenticated,
  });

  // Filter and prepare form options
  const formOptions = useMemo(() => {
    let filteredForms = forms;

    // Apply custom filter if provided
    if (formFilter) {
      filteredForms = filteredForms.filter(formFilter);
    }

    // Sort by name for better UX
    filteredForms.sort((a, b) => {
      const nameA = a.name || "Unnamed Form";
      const nameB = b.name || "Unnamed Form";
      return nameA.localeCompare(nameB);
    });

    // Convert to select options
    return filteredForms.map((form) => ({
      value: form.id || "",
      label: form.name || "Unnamed Form",
      disabled: form.status !== STATUSES.ACTIVE,
    }));
  }, [forms, includeInactive, formFilter]);

  // Create a map for quick form lookup by ID
  const formMap = useMemo(() => {
    const map = new Map<string, Form>();
    forms.forEach((form) => {
      if (form.id) {
        map.set(form.id, form);
      }
    });
    return map;
  }, [forms]);

  // Auto-select first form if required and no value provided
  useEffect(() => {
    if (required && !value && formOptions.length > 0 && !loading && onChange) {
      const firstForm = formOptions[0];
      if (firstForm && !firstForm.disabled) {
        const formObject = formMap.get(firstForm.value);
        onChange(firstForm.value, formObject);
      }
    }
  }, [required, value, formOptions.length, loading]);

  // Handle onChange with form object lookup
  const handleChange = (selectedValue: string | null) => {
    if (selectedValue) {
      const selectedForm = formMap.get(selectedValue);
      onChange?.(selectedValue, selectedForm);
    } else {
      onChange?.(null, undefined);
    }
  };

  // Determine select state
  const isInitialLoading = loading && forms.length === 0;
  const hasError = lastError && !loading;
  const isEmptyState = isEmpty && !loading;

  const selectState = {
    data: isInitialLoading || hasError || isEmptyState ? [] : formOptions,
    placeholder: isInitialLoading
      ? loadingPlaceholder
      : hasError
      ? "Error loading forms"
      : isEmptyState
      ? emptyText
      : placeholder,
    disabled: disabled || isInitialLoading || hasError || isEmptyState,
    error: hasError ? error || "Failed to load forms" : error,
    rightSection:
      isInitialLoading || (loading && forms.length > 0) ? (
        <LoadingIndicator size={16} color="var(--mantine-color-brand-filled)" />
      ) : (
        selectProps.rightSection
      ),
  };

  return (
    <Select
      {...selectProps}
      size={size}
      data={selectState.data}
      value={value ?? null}
      onChange={handleChange}
      placeholder={selectState.placeholder}
      clearable={!required}
      searchable
      required={required}
      disabled={selectState.disabled}
      error={selectState.error}
      rightSection={selectState.rightSection}
    />
  );
};
